import { useEffect } from 'react';
import Box from '@material-ui/core/Box';
import Checkbox from '@material-ui/core/Checkbox';
import FormControl from '@material-ui/core/FormControl';
import FormControlLabel from '@material-ui/core/FormControlLabel';
import FormHelperText from '@material-ui/core/FormHelperText';
import { FieldExtensionComponentProps } from '@backstage/plugin-scaffolder-react';

/** What a given integration needs before it can do anything at all. */
export type IntegrationRequirement =
  /** Auth0 protects an API service or a server-rendered app; a SPA alone has
   *  nothing to protect, and the auth0-* steps are gated on the same condition. */
  | 'api_or_ssr'
  /** Grafana dashboards and Azure repositories both need generated SOURCE —
   *  a web frontend, an API service or a worker. */
  | 'source'
  /** Not a checkbox: the note shown when the page has nothing left to offer. */
  | 'empty_note';

export interface IntegrationsFormData {
  enable_static_web?: boolean;
  enable_app_service?: boolean;
  enable_container_app_jobs?: boolean;
  frontend?: string;
}

const SSR_FRONTENDS = ['nextjs', 'aspnet'];

/** True when the wizard will generate a repository's worth of code. */
export function hasSource(form: IntegrationsFormData): boolean {
  return !!(
    form.enable_static_web ||
    form.enable_app_service ||
    form.enable_container_app_jobs
  );
}

/**
 * Whether an integration is worth offering for the app being described.
 *
 * Everything here was already true of the STEPS — `auth0-*` has always been
 * gated on `enable_auth0 and (app_service or ssr)`, `grafana:provision` returns
 * early when there is no dashboard file, and every `ensure-*-repo` is gated on
 * its own capability. What was missing is saying so on the form: a checkbox that
 * cannot change the outcome is worse than no checkbox, because ticking it looks
 * like a decision.
 */
export function shouldShowIntegration(
  requirement: IntegrationRequirement,
  form: IntegrationsFormData,
): boolean {
  switch (requirement) {
    case 'api_or_ssr':
      return !!(
        form.enable_app_service ||
        (form.enable_static_web && SSR_FRONTENDS.includes(form.frontend ?? ''))
      );
    case 'source':
      return hasSource(form);
    // The note is the mirror image: it appears exactly when nothing else does.
    case 'empty_note':
      return !hasSource(form);
    default:
      return true;
  }
}

/**
 * DEFAULT for whether hiding an integration should also force its value false.
 * A field can override it with `ui:options.clearWhenHidden`.
 *
 * Clearing is about honesty, not behaviour: these steps are already no-ops
 * without a target, so the value only decides what gets RECORDED — and an app
 * whose catalog entry and "ready" email claim "auth0, grafana" when neither
 * exists is a lie told by a default. Auth0 is cleared here; Grafana opts in from
 * the template, since it shares the `source` requirement with the repositories.
 *
 * `source` does not clear by default, because of `enable_azure_repo`. That flag
 * is the user's choice of how source is delivered, not an integration that
 * exists or not: with no source every `ensure-*-repo` step is already skipped,
 * and keeping the value means the choice is still there when a capability is
 * turned back on.
 */
export function shouldClearWhenHidden(
  requirement: IntegrationRequirement,
): boolean {
  return requirement === 'api_or_ssr';
}

export const PlatformIntegrationField = (
  props: FieldExtensionComponentProps<boolean>,
) => {
  const { formData, onChange, schema, uiSchema, idSchema } = props;
  const form =
    ((props as any).formContext?.formData as IntegrationsFormData | undefined) ??
    {};

  const requirement = (uiSchema?.['ui:options']?.requires ??
    'source') as IntegrationRequirement;
  const clearWhenHidden = (uiSchema?.['ui:options']?.clearWhenHidden ??
    shouldClearWhenHidden(requirement)) as boolean;
  const visible = shouldShowIntegration(requirement, form);
  const isNote = requirement === 'empty_note';
  const uncheckedWarning = uiSchema?.['ui:options']?.uncheckedWarning as
    | string
    | undefined;

  useEffect(() => {
    if (isNote || visible || !clearWhenHidden || formData === false) return;
    onChange(false);
  }, [isNote, visible, clearWhenHidden, formData, onChange]);

  if (isNote) {
    if (!visible) return null;
    return (
      <Box mt={1}>
        <FormHelperText>
          No web frontend, API service or job was selected, so no application
          code is generated — and there is nothing here to integrate with. You
          can skip this step.
        </FormHelperText>
      </Box>
    );
  }

  if (!visible) return null;

  return (
    <FormControl fullWidth margin="normal">
      {schema.description && (
        <FormHelperText>{schema.description}</FormHelperText>
      )}
      <FormControlLabel
        control={
          <Checkbox
            id={idSchema?.$id}
            checked={!!formData}
            onChange={event => onChange(event.target.checked)}
            color="primary"
          />
        }
        label={schema.title ?? ''}
      />
      {uncheckedWarning && !formData && (
        <FormHelperText error>{uncheckedWarning}</FormHelperText>
      )}
    </FormControl>
  );
};
